<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import suiteAPI from 'dashboard/api/integrations/suite';

const props = defineProps({
  modelValue: {
    type: [Number, String, null],
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const suiteEnabled = ref(false);
const loading = ref(false);
const extensions = ref([]);
const boundExtensionsByRamalId = ref({});
const selectedExtensionMeta = ref(null);

const selected = computed({
  get: () => (props.modelValue == null ? '' : props.modelValue),
  set: value => emit('update:modelValue', value === '' ? null : value),
});

const extensionLabel = extension => {
  if (extension.numero) {
    return `${extension.nome} (${extension.numero})`;
  }
  return extension.nome;
};

const availableExtensions = computed(() =>
  extensions.value.filter(extension => extension.has_senha_sip)
);

const extensionOption = extension => {
  const ramalId = Number(extension.ramal_id);
  const boundAgent = boundExtensionsByRamalId.value[ramalId];
  const isBoundElsewhere =
    Boolean(boundAgent) && Number(props.modelValue) !== ramalId;
  let label = extensionLabel(extension);
  if (isBoundElsewhere) {
    label = `${label} - ${t('AGENT_MGMT.FORM.SUITE_EXTENSION.BOUND', {
      agent: boundAgent,
    })}`;
  }

  return {
    value: ramalId,
    label,
    disabled: isBoundElsewhere,
  };
};

const extensionOptions = computed(() => {
  const options = [
    {
      value: '',
      label: t('AGENT_MGMT.FORM.SUITE_EXTENSION.NONE'),
    },
    ...availableExtensions.value.map(extensionOption),
  ];

  const selectedId = Number(props.modelValue);
  if (
    selectedId &&
    !options.some(option => option.value === selectedId) &&
    selectedExtensionMeta.value
  ) {
    options.splice(1, 0, extensionOption(selectedExtensionMeta.value));
  }

  return options;
});

const fetchExtensions = async (query = '') => {
  const params = {};
  const term = query?.trim();
  if (term) params.busca = term;

  const { data } = await suiteAPI.extensions(params);
  extensions.value = data.extensions || [];
  boundExtensionsByRamalId.value = (data.bound_extensions || []).reduce(
    (acc, item) => {
      acc[Number(item.ramal_id)] = item.agent_name;
      return acc;
    },
    {}
  );
};

const loadExtensions = async () => {
  loading.value = true;
  try {
    const statusResponse = await suiteAPI.status();
    const connected =
      statusResponse.data?.connected &&
      !statusResponse.data?.needs_company_selection &&
      statusResponse.data?.cliente_id;
    suiteEnabled.value = Boolean(connected);
    if (!connected) return;

    await fetchExtensions();
  } catch {
    suiteEnabled.value = false;
  } finally {
    loading.value = false;
  }
};

const searchExtensions = debounce(query => {
  fetchExtensions(query);
}, 300);

watch(
  () => props.modelValue,
  id => {
    if (!id) {
      selectedExtensionMeta.value = null;
      return;
    }
    const found = extensions.value.find(
      extension => Number(extension.ramal_id) === Number(id)
    );
    if (found) {
      selectedExtensionMeta.value = found;
    }
  },
  { immediate: true }
);

watch(extensions, list => {
  if (!props.modelValue) return;
  const found = list.find(
    extension => Number(extension.ramal_id) === Number(props.modelValue)
  );
  if (found) {
    selectedExtensionMeta.value = found;
  }
});

onMounted(loadExtensions);
</script>

<template>
  <div v-if="suiteEnabled" class="w-full flex flex-col gap-1">
    <label class="text-sm text-n-slate-12">
      {{ t('AGENT_MGMT.FORM.SUITE_EXTENSION.LABEL') }}
    </label>
    <ComboBox
      v-model="selected"
      appearance="form"
      use-api-results
      :disabled="loading"
      :options="extensionOptions"
      :placeholder="t('AGENT_MGMT.FORM.SUITE_EXTENSION.NONE')"
      :search-placeholder="t('AGENT_MGMT.FORM.SUITE_EXTENSION.SEARCH')"
      :empty-state="t('AGENT_MGMT.FORM.SUITE_EXTENSION.EMPTY')"
      :message="t('AGENT_MGMT.FORM.SUITE_EXTENSION.HELP')"
      @search="searchExtensions"
    />
  </div>
</template>
